// ============================================================
// ERROR MAPPING
// Turn contract custom errors + wallet errors into friendly text.
// Users should NEVER see raw JSON blobs or "execution reverted (unknown
// custom error, args=[…])" strings. Every path here ends in a short,
// plain-English sentence.
// ============================================================

// Custom-error name → friendly message.
// Covers PlanLogic, PlanRewards, StorageCore, and PlanAdmin errors.
const CUSTOM_ERROR_MAP = {
  E_OW:         'Only the contract owner can perform this action.',
  E_OA:         'Only the admin can perform this action.',
  E_OOA:        'Only owner or admin can perform this action.',
  E_ADM:        'Only the admin contract may call this.',
  E_IA:         'Invalid address.',
  E_NR:         'You are not registered yet. Register first, then invest.',
  E_REG:        'This wallet is already registered.',
  E_IR:         'Invalid referrer — the referrer ID must be an active investor.',
  E_BL:         'This account is blacklisted.',
  E_MIN:        'Amount is below the minimum required.',
  E_PLAN:       'Invalid plan for this action.',
  E_LOCK:       'PinkSale lock verification failed. Check lock owner, amount, and duration.',
  E_IPT:        'Invalid plan type.',
  E_II:         'Invalid investment index.',
  E_IC:         'Invalid contributor index.',
  E_NA:         'This item is not active.',
  E_NAV:        'Nothing available to claim right now — wait for the next cutoff or sync first.',
  E_PRICE:      'GRX price feed unavailable. Try again in a moment.',
  E_REN:        'Reentrancy detected. Please try again.',
  E_MAX_USERS:  'Maximum user cap reached.',
  E_NRI:        'This is not a flexible investment — locked principals must wait for lock expiry.',
  E_AW:         'Principal has already been withdrawn.',
  E_IPR:        'Invalid parameter.',
  E_OL:         'Only an authorized logic contract may call this.',
  E_OOL:        'Only owner or admin may call this.',
  E_PAUSED:     'The contract is currently paused.',
  E_NOT_PAUSED: 'The contract must be paused for this action.',
  E_TF:         'Token transfer failed. Check your balance and allowance.',
  E_IU:         'Invalid user.',
  E_REW:        'Only the rewards contract may call this.',
};

// 4-byte selector → known error name (for BSC RPCs that return `data`).
// Pre-computed keccak256 selectors of all our custom errors.
const SELECTOR_MAP = {
  '0x676ea22b': 'E_OW',
  '0x9c8875a9': 'E_ADM',
  '0xc9d4f38d': 'E_IA',
  '0x3d693e21': 'E_NR',
  '0xf98d0d0b': 'E_REG',
  '0xd0b0dfd0': 'E_IR',
  '0x1ff6f9f2': 'E_BL',
  '0xc3ada2c6': 'E_MIN',
  '0x2be74d0a': 'E_LOCK',
  '0xf3aac03d': 'E_IPT',
  '0xa1dea86b': 'E_II',
  '0x77e9baf7': 'E_IC',
  '0x5eaf59a5': 'E_NA',
  '0x5c2f6c68': 'E_NAV',
  '0xd4a03ab8': 'E_PRICE',
  '0x4c68aad0': 'E_REN',
  '0x03e04ff3': 'E_NRI',
  '0x9c33fca8': 'E_AW',
  '0x2fe6b56b': 'E_IPR',
  '0x50beaf7c': 'E_OL',
  '0x8b3d13f6': 'E_OOL',
  '0xab35696f': 'E_PAUSED',
  '0x8e56a3c7': 'E_NOT_PAUSED',
  '0x90b8ec18': 'E_TF',
  '0x82b0f9df': 'E_IU',
};

// Recursively walk error object looking for a string field at path.
const pick = (obj, ...paths) => {
  for (const path of paths) {
    let cur = obj;
    for (const k of path.split('.')) {
      if (cur == null) { cur = null; break; }
      cur = cur[k];
    }
    if (cur != null) return cur;
  }
  return null;
};

const findSelectorInText = (text) => {
  if (!text) return null;
  const m = String(text).match(/0x[0-9a-fA-F]{8}/g);
  if (!m) return null;
  for (const sel of m) {
    if (SELECTOR_MAP[sel.toLowerCase()]) return SELECTOR_MAP[sel.toLowerCase()];
  }
  return null;
};

const findErrorNameInText = (text) => {
  if (!text) return null;
  const s = String(text);
  for (const key of Object.keys(CUSTOM_ERROR_MAP)) {
    // "E_MIN()", "'E_MIN'", " E_MIN ", "error E_MIN", "E_MIN,"
    const re = new RegExp('\\b' + key + '\\b');
    if (re.test(s)) return key;
  }
  return null;
};

export const parseError = (err) => {
  if (!err) return 'Unknown error.';

  // ── 1. User-rejected / wallet-state errors (codes first, cheapest match) ──
  const code = err.code || err?.info?.error?.code || err?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED') return 'Transaction rejected in your wallet.';
  if (code === -32002) return 'A wallet request is already pending — open your wallet to approve it.';
  if (code === -32603) {
    // Generic "internal JSON-RPC error" — fall through so we can dig into data.
  }
  if (code === 'NETWORK_ERROR') return 'Network error — check your connection or try a different RPC.';
  if (code === 'TIMEOUT') return 'Request timed out — the RPC may be slow, try again.';
  if (code === 'UNSUPPORTED_OPERATION') return 'This operation is not supported by your wallet.';
  if (code === 'CALL_EXCEPTION') {
    // Fall through — will be handled by selector/name matching below.
  }
  if (code === 'INSUFFICIENT_FUNDS') return 'Not enough BNB to pay for gas.';

  // ── 2. Structured error name from ethers v6 (revert with custom error) ──
  const errName = pick(err,
    'errorName',
    'info.error.data.errorName',
    'info.error.errorName',
    'data.errorName',
    'cause.errorName',
    'revert.name'
  );
  if (errName && CUSTOM_ERROR_MAP[errName]) return CUSTOM_ERROR_MAP[errName];

  // ── 3. Revert data (0x...) — try to match selector ──
  const revertData = pick(err,
    'data',
    'info.error.data.data',
    'info.error.data',
    'error.data',
    'cause.data'
  );
  if (typeof revertData === 'string' && revertData.length >= 10) {
    const sel = revertData.slice(0, 10).toLowerCase();
    if (SELECTOR_MAP[sel]) return CUSTOM_ERROR_MAP[SELECTOR_MAP[sel]];
  }

  // ── 4. Flatten the whole thing into text and search ──
  const flat = (() => {
    try {
      return [
        err.shortMessage,
        err.reason,
        err.message,
        err.info?.error?.message,
        err.info?.error?.data?.message,
        err.cause?.shortMessage,
        err.cause?.message,
        err.data?.message,
        // Last-resort JSON blob (rare, but pulls things out of odd RPC shapes)
        (() => { try { return JSON.stringify(err); } catch { return ''; } })(),
      ].filter(Boolean).join(' | ');
    } catch { return String(err); }
  })();

  const flatLower = flat.toLowerCase();

  // Common wallet / RPC textual patterns
  if (flatLower.includes('insufficient funds')) return 'Not enough BNB to pay for gas.';
  if (flatLower.includes('insufficient allowance')) return 'Token allowance is too low — approve first, then retry.';
  if (flatLower.includes('transfer amount exceeds balance')) return 'Not enough token balance for this action.';
  if (flatLower.includes('transfer amount exceeds allowance')) return 'Token allowance is too low — approve first, then retry.';
  if (flatLower.includes('user rejected') || flatLower.includes('user denied')) return 'Transaction rejected in your wallet.';
  if (flatLower.includes('gas required exceeds allowance')) return 'Gas limit too low — try again with a higher gas limit.';
  if (flatLower.includes('nonce too low') || flatLower.includes('nonce too high')) return 'Wallet nonce is out of sync — reset the account in your wallet, or try again.';
  if (flatLower.includes('replacement transaction underpriced')) return 'A pending tx from this wallet has the same nonce. Wait for it to confirm or cancel it.';
  if (flatLower.includes('already known')) return 'This transaction was already submitted — waiting for it to mine.';
  if (flatLower.includes('failed to fetch') || flatLower.includes('network request failed')) return 'RPC unreachable — switch your wallet\'s network RPC and retry.';
  if (flatLower.includes('cors') || flatLower.includes('blocked by cors')) return 'RPC blocked the request. Try switching to a different network RPC.';
  if (flatLower.includes('chain mismatch') || flatLower.includes('wrong network') || flatLower.includes('unrecognized chain id')) return 'Wallet is on the wrong network — switch to BSC.';
  if (flatLower.includes('execution reverted') && !flatLower.match(/[a-z_]{2,}\(\)/)) {
    // Reverted but no custom error name we can find — see if there's a revert-string
    const reason = flat.match(/reverted with reason string ['"](.+?)['"]/);
    if (reason) return reason[1];
    return 'Transaction reverted on-chain. Please check your inputs and try again.';
  }
  if (flatLower.includes('missing revert data')) return 'The transaction would fail. Check balances, allowances, and inputs.';

  // ── 5. Match custom error selector / name found anywhere in the text ──
  const bySelector = findSelectorInText(flat);
  if (bySelector) return CUSTOM_ERROR_MAP[bySelector];
  const byName = findErrorNameInText(flat);
  if (byName) return CUSTOM_ERROR_MAP[byName];

  // ── 6. Revert-with-reason string ──
  const reasonMatch = flat.match(/reverted with reason string ['"](.+?)['"]/)
                    || flat.match(/reason=['"]?([^'"),]+)/);
  if (reasonMatch) return reasonMatch[1];

  // ── 7. Best-effort short message from the error itself ──
  const short = err.shortMessage || err.reason;
  if (short && typeof short === 'string' && short.length < 200 && !short.includes('{')) return short;

  // ── 8. Absolute fallback — never return a JSON blob ──
  const msg = err.message ? String(err.message) : '';
  if (msg && !msg.includes('{') && msg.length < 200) return msg;
  return 'Something went wrong. Please try again.';
};
